import { useMemo } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { Button } from '@/components/shadcn/button'
import { ChevronRight, ChevronDown } from 'lucide-react'
import { useDraggable } from '@dnd-kit/core'
import { IconDragHandleOutline } from '@icons/IconDragHandleOutline'
import { IconArrowLeftSmallOutline } from '@icons/IconArrowLeftSmallOutline'
import { IconDeleteOutline } from '@icons/IconDeleteOutline'
import { DataTableV2DataRow } from '../types'
import { TooltipButton } from '@/components/ui/TooltipButton'

interface UseExpanderColumnV2Props {
  columns: ColumnDef<DataTableV2DataRow, unknown>[]
  isGroupingEnabled: boolean
  isRegrouping?: boolean
  onUngroup?: (rowId: string) => void
  onDeleteGroup?: (rowId: string) => void
}

export function useExpanderColumnV2({
  columns,
  isGroupingEnabled,
  isRegrouping = false,
  onUngroup,
  onDeleteGroup,
}: UseExpanderColumnV2Props) {
  return useMemo(() => {
    if (!isGroupingEnabled) return columns

    const expanderColumn: ColumnDef<DataTableV2DataRow, unknown> = {
      id: 'expander',
      size: 24,
      header: () => null,
      cell: ({ row }) => {
        const rowData = row.original as DataTableV2DataRow & {
          isGroup?: boolean
          subRows?: DataTableV2DataRow[]
        }
        const isGroup = rowData?.isGroup === true
        const isSubRow = row.depth > 0
        const isOrphan = !isGroup && !isSubRow
        const rowId = String(rowData?.id || row.id)
        const subRows = row.subRows || []
        const hasSubRows = subRows.length > 0

        // If regrouping mode is active (only when grouping is enabled)
        if (isRegrouping && isGroupingEnabled) {
          // Show ungroup button for subrows
          if (isSubRow) {
            return (
              <TooltipButton tooltip="Remove from group">
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-6 p-0 hover:bg-transparent"
                  onClick={() => onUngroup?.(rowId)}
                >
                  <IconArrowLeftSmallOutline className="size-4" />
                </Button>
              </TooltipButton>
            )
          }

          // Show drag handle for orphan rows
          if (isOrphan) {
            // eslint-disable-next-line react-hooks/rules-of-hooks
            const { attributes, listeners, setNodeRef } = useDraggable({
              id: rowId,
            })

            return (
              <TooltipButton tooltip="Drag to another group or row">
                <Button
                  ref={setNodeRef}
                  variant="ghost"
                  size="icon"
                  className="size-6 cursor-move touch-none select-none p-0 hover:bg-transparent"
                  {...listeners}
                  {...attributes}
                >
                  <IconDragHandleOutline className="size-4" />
                </Button>
              </TooltipButton>
            )
          }

          // For group rows
          if (isGroup) {
            // If empty group, show delete button
            if (!hasSubRows) {
              return (
                <TooltipButton tooltip="Remove empty group">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-6 p-0 hover:bg-transparent hover:text-destructive"
                    onClick={() => onDeleteGroup?.(rowId)}
                  >
                    <IconDeleteOutline className="size-4" />
                  </Button>
                </TooltipButton>
              )
            }

            // If expandable group, show expander
            if (row.getCanExpand()) {
              return (
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-6 p-0 hover:bg-transparent"
                  onClick={row.getToggleExpandedHandler()}
                >
                  {row.getIsExpanded() ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
                </Button>
              )
            }
          }

          // Return placeholder to maintain consistent row height
          return <div className="size-6" />
        }

        // Normal mode: show expander for expandable rows
        if (!row.getCanExpand()) {
          // Return placeholder to maintain consistent row height
          return <div className="size-6" />
        }

        return (
          <Button
            variant="ghost"
            size="icon"
            className="size-6 p-0 hover:bg-transparent"
            onClick={row.getToggleExpandedHandler()}
          >
            {row.getIsExpanded() ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
          </Button>
        )
      },
      enableSorting: false,
      enableHiding: false,
    }

    return [expanderColumn, ...columns]
  }, [columns, isGroupingEnabled, isRegrouping, onUngroup, onDeleteGroup])
}
